import type { QuotaQuery } from '@hive/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import bytes from 'bytes';
import { EMPTY, map, mergeMap } from 'rxjs';

import type { AuthService } from '../services/auth';
import type { UserQuotaStore } from '../stores/user-quota';

export class UserQuota extends Entity {
  quota$ = new LiveData<NonNullable<QuotaQuery['currentUser']>['quota'] | null>(
    null
  );
  /** Used storage in bytes */
  used$ = new LiveData<number | null>(null);
  /** Formatted used storage */
  usedFormatted$ = this.used$.map(used =>
    used !== null ? bytes.format(used) : null
  );
  /** Maximum storage limit in bytes */
  max$ = this.quota$.map(quota => (quota ? quota.storageQuota : null));
  /** Maximum storage limit formatted */
  maxFormatted$ = this.max$.map(max =>
    max !== null ? bytes.format(max) : null
  );

  /** Percentage of storage used */
  percent$ = LiveData.computed(get => {
    const max = get(this.max$);
    const used = get(this.used$);
    if (max === null || used === null) {
      return null;
    }
    // If max is 0 (no subscription/trial required), show 100% to indicate storage is unavailable
    if (max === 0) {
      return used > 0 ? 100 : 0;
    }
    return Math.min(
      100,
      Math.max(0.5, Number(((used / max) * 100).toFixed(4)))
    );
  });

  color$ = this.percent$.map(percent =>
    percent !== null
      ? percent > 80
        ? cssVar('errorColor')
        : cssVar('processingColor')
      : null
  );

  /** Plan name from quota */
  planName$ = this.quota$.map(quota => quota?.humanReadable?.name ?? null);

  /** Whether the user has a paid/lifetime plan (not free) */
  hasPaidPlan$ = this.quota$.map(quota => {
    if (!quota) return null;
    const name = quota.humanReadable?.name?.toLowerCase() ?? '';
    // Check if the plan name indicates a paid plan
    return (
      name.includes('lifetime') ||
      name.includes('pro') ||
      name.includes('team') ||
      (quota.storageQuota > 0 &&
        !name.includes('free') &&
        !name.includes('trial'))
    );
  });

  /** Whether the user is on a lifetime plan */
  isLifetimePlan$ = this.quota$.map(quota => {
    if (!quota) return null;
    const name = quota.humanReadable?.name?.toLowerCase() ?? '';
    return name.includes('lifetime');
  });

  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly store: UserQuotaStore
  ) {
    super();
  }

  revalidate = effect(
    map(() => ({
      accountId: this.authService.session.account$.value?.id,
    })),
    exhaustMapSwitchUntilChanged(
      (a, b) => a.accountId === b.accountId,
      ({ accountId }) =>
        fromPromise(async signal => {
          if (!accountId) {
            return; // no quota if no user
          }
          const { quota, used } = await this.store.fetchUserQuota(signal);

          return { quota, used };
        }).pipe(
          smartRetry(),
          mergeMap(data => {
            if (data) {
              const { quota, used } = data;
              this.quota$.next(quota);
              this.used$.next(used);
            } else {
              this.quota$.next(null);
              this.used$.next(null);
            }
            return EMPTY;
          }),
          catchErrorInto(this.error$),
          onStart(() => this.isRevalidating$.next(true)),
          onComplete(() => this.isRevalidating$.next(false))
        ),
      () => {
        // Reset the state when the user is changed
        this.reset();
      }
    )
  );

  reset() {
    this.quota$.next(null);
    this.used$.next(null);
    this.error$.next(null);
    this.isRevalidating$.next(false);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
